import React, { useEffect, useState, useRef } from "react";
import { X, Send, Paperclip } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const STATUS_STYLE = {
  "new":         "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  "in progress": "text-blue-400 bg-blue-500/10 border-blue-500/20",
  "waiting":     "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "resolved":    "text-white/30 bg-white/5 border-white/10",
  "closed":      "text-white/20 bg-white/5 border-white/5",
};

const SLA_STYLE = {
  OK:       "text-[#00ff90]/50",
  AT_RISK:  "text-yellow-400",
  BREACHED: "text-red-400",
};

export default function TicketDrawer({ ticket, onClose, onUpdate }) {
  const [tab,        setTab]        = useState("thread");
  const [thread,     setThread]     = useState([]);
  const [threadLoad, setThreadLoad] = useState(false);
  const [message,    setMessage]    = useState("");
  const [visibility, setVisibility] = useState("public");
  const [file,       setFile]       = useState(null);
  const [busy,       setBusy]       = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!ticket?.id) return;
    setThreadLoad(true);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/tickets/thread/${ticket.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setThread(Array.isArray(d) ? d : []))
      .catch(() => setThread([]))
      .finally(() => setThreadLoad(false));
  }, [ticket?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  if (!ticket) return null;

  const statusKey = (ticket.status || "").toLowerCase();
  const statusStyle = STATUS_STYLE[statusKey] || "text-white/40 bg-white/5 border-white/10";

  const handleReply = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", message);
      fd.append("visibility", visibility);
      if (file) fd.append("attachment", file);
      const res = await fetch(`${API_BASE}/api/tickets/reply/${ticket.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      const entry = await res.json();
      setThread(t => [...t, entry]);
      setMessage("");
      setFile(null);
    } catch {}
    finally { setBusy(false); }
  };

  const handleAction = async (patch) => {
    const key = Object.keys(patch)[0];
    setActionBusy(key);
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/tickets/update-status/${ticket.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      onUpdate?.(patch);
    } catch {}
    finally { setActionBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="px-5 py-4 border-b border-white/5 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs font-mono text-white/30">{ticket.id}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{ticket.status}</span>
                {ticket.sla && ticket.sla !== "OK" && (
                  <span className={`text-xs ${SLA_STYLE[ticket.sla] || ""}`}>
                    {ticket.sla === "BREACHED" ? "⚠ SLA Breached" : "⚠ SLA At Risk"}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-white">{ticket.title}</h3>
              <p className="text-xs text-white/30 mt-0.5">{ticket.category} · {ticket.priority}</p>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors shrink-0">
              <X size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-3">
            {["thread","details","actions"].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
                  tab === t
                    ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                    : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
                }`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* Thread tab */}
          {tab === "thread" && (
            <div className="flex flex-col h-full">
              <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                {threadLoad && (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />)}</div>
                )}
                {!threadLoad && thread.length === 0 && (
                  <p className="text-white/20 text-sm italic text-center py-6">No messages yet.</p>
                )}
                {thread.map((msg, i) => {
                  const isInternal = msg.visibility === "internal";
                  const isOwn = msg.sender_type === "user" || msg.is_own;
                  return (
                    <div key={i} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
                        isInternal ? "bg-yellow-500/5 border border-yellow-500/15" :
                        isOwn      ? "bg-[#00ff90]/8 border border-[#00ff90]/20" :
                                     "bg-white/5 border border-white/8"
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-white/30">{msg.sender || "Support"}</span>
                          {isInternal && <span className="text-xs text-yellow-400/50 border border-yellow-500/20 px-1.5 py-0.5 rounded">internal</span>}
                          <span className="text-xs text-white/20">{(msg.timestamp || "").replace("T"," ").slice(0,16)}</span>
                        </div>
                        <p className="text-sm text-white/80 whitespace-pre-wrap">{msg.message}</p>
                        {msg.attachment_url && (
                          <a href={msg.attachment_url} target="_blank" rel="noreferrer"
                            className="text-xs text-[#00ff90]/60 hover:text-[#00ff90] mt-1 block">📎 View attachment</a>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Reply bar */}
              <div className="p-4 border-t border-white/5 space-y-2 shrink-0">
                <div className="flex gap-2">
                  <select value={visibility} onChange={e => setVisibility(e.target.value)}
                    className="bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none">
                    <option value="public">Public reply</option>
                    <option value="internal">Internal note</option>
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <textarea value={message} onChange={e => setMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                    placeholder={visibility === "internal" ? "Internal note…" : "Public reply…"}
                    rows={2}
                    className="flex-1 bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors resize-none placeholder-white/20" />
                  <div className="flex flex-col gap-1.5">
                    <label className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-white cursor-pointer transition-colors">
                      <Paperclip size={13} />
                      <input type="file" className="hidden" onChange={e => setFile(e.target.files[0])} />
                    </label>
                    <button onClick={handleReply} disabled={busy || !message.trim()}
                      className="w-8 h-8 rounded-lg bg-[#00ff90]/15 border border-[#00ff90]/30 text-[#00ff90] hover:bg-[#00ff90]/25 disabled:opacity-30 transition-colors flex items-center justify-center">
                      <Send size={13} />
                    </button>
                  </div>
                </div>
                {file && <p className="text-xs text-white/30 truncate">📎 {file.name}</p>}
              </div>
            </div>
          )}

          {/* Details tab */}
          {tab === "details" && (
            <div className="p-5 space-y-3">
              {[
                ["Requester", ticket.requester],
                ["Assignee",  ticket.assignee  || "—"],
                ["Category",  ticket.category  || "—"],
                ["Priority",  ticket.priority  || "—"],
                ["SLA",       ticket.sla       || "—"],
                ["Created",   (ticket.created  || "").replace("T"," ").slice(0,16)],
                ["Updated",   (ticket.updated  || "").replace("T"," ").slice(0,16)],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{label}</span>
                  <span className="text-sm text-white/70">{value}</span>
                </div>
              ))}
              <p className="text-xs text-white/20 pt-2">Work context only — personal data not shown.</p>
            </div>
          )}

          {/* Actions tab */}
          {tab === "actions" && (
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "In Progress", patch: { status: "In Progress" }, style: "border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400" },
                    { label: "Resolved",    patch: { status: "Resolved"    }, style: "border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90]" },
                    { label: "Waiting",     patch: { status: "Waiting"     }, style: "border-yellow-500/25 bg-yellow-500/8 text-yellow-400/70 hover:bg-yellow-500/15 hover:text-yellow-400" },
                    { label: "Close",       patch: { status: "Closed"      }, style: "border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400" },
                  ].map(({ label, patch, style }) => (
                    <button key={label} onClick={() => handleAction(patch)}
                      disabled={actionBusy !== null}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${style}`}>
                      {actionBusy === Object.keys(patch)[0] ? "Updating…" : label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Set Priority</p>
                <div className="flex gap-2">
                  {["P1","P2","P3","P4"].map(p => (
                    <button key={p} onClick={() => handleAction({ priority: p })}
                      disabled={actionBusy !== null}
                      className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}